import type { Table } from 'dexie'
import { nowUtcIso } from '@/lib/dates'
import type { WorkspaceDb } from './db'
import type { OutboxOperation } from './types'

/**
 * Operation/entity IDs are generated on the client BEFORE the local save so the same
 * UUID can be retried idempotently after an unknown-outcome transport failure
 * (plan sections 7 and 16).
 */
export function newUuid(): string {
  const c = globalThis.crypto as Crypto | undefined
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  // Fallback (very old environments): RFC4122-ish from getRandomValues.
  const bytes = new Uint8Array(16)
  c?.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export interface NewOperationInput {
  workspace_id: string
  entity_id: string
  entity_type: string
  expected_revision: number | null
  action: string
  payload: Record<string, unknown>
}

export function buildOperation(input: NewOperationInput): OutboxOperation {
  return {
    operation_id: newUuid(),
    workspace_id: input.workspace_id,
    entity_id: input.entity_id,
    entity_type: input.entity_type,
    expected_revision: input.expected_revision,
    action: input.action,
    payload: input.payload,
    created_at: nowUtcIso(),
    attempts: 0,
    last_error: null,
  }
}

/**
 * Write a local change AND its outbox operation in ONE IndexedDB transaction, so a
 * record can never be saved without its queued mutation (plan section 8). `db.outbox`
 * is always included in the transaction scope.
 */
export async function atomicLocalWrite<T>(
  db: WorkspaceDb,
  tables: Table[],
  work: () => Promise<T>,
  op: OutboxOperation,
): Promise<T> {
  return db.transaction('rw', [...tables, db.outbox], async () => {
    const result = await work()
    await db.outbox.add(op)
    return result
  })
}

/** Enqueue an operation on its own (when the record write already happened in scope). */
export async function enqueueOperation(db: WorkspaceDb, op: OutboxOperation): Promise<void> {
  await db.outbox.add(op)
}

export async function pendingCount(db: WorkspaceDb): Promise<number> {
  return db.outbox.count()
}
